import React, { useCallback, useEffect, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Grid, Table } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import cloneDeep from 'lodash.clonedeep'
import InfoPopup from '~/components/InfoPopup'
import LinkPopup from '../LinkPopup'
import ErrorIcon from '../ErrorIcon'

interface Props {
  calfRearingTypes: {key: number, label: string}[]
  isLoadingPredefinedData: boolean
}

const VOL_KEY = 2
const MAT_KEY = 1
const VOL_MAT_KEY = 3
const TableRearing: React.FC<Props> = ({ calfRearingTypes, isLoadingPredefinedData }) => {

  const { t } = useTranslation()
  const { control, getValues } = useFormContext()
  const watchWeaningWeeks = useWatch({ control, name: 'weaningWeeks' })
  const watchCalfRearing = useWatch({ control, name: 'calfRearing' })

  const { fields } = useFieldArray({
    control,
    name: 'weaningWeeksValues',
    keyName: 'key'
  })

  const defaltTableColumHeaders = [
    {
      label: t('Feeding section'),
      key: 'feedingSection',
      info: 'Bitte geben Sie die durchschnittlich verfütterte Menge pro Kalb und Tag ein. Es ist automatisch berücksichtigt, dass die männlichen Kälber den Betrieb mit 4 Wochen verlassen.',
      dataSource: 'Die vorgegebenen Werte sind lediglich Vorschlagswerte. Abweichende Tränke-/Futtermengen sollen in der Tabelle überschrieben werden.',
      unit: 'l/Tag',
      warningMax: 15,
      errorMax: 20
    },
    {
      label: calfRearingTypes[0].label,
      key: 'wholeMilk',
      optionKey: calfRearingTypes[0]?.key,
      info: 'l Vollmilch je Tier und Tag',
      dataSource: 'Tränkekurve',
      warningMax: 15,
      unit: 'l',
      errorMax: 20
    },
    {
      label: calfRearingTypes[1].label,
      key: 'milkReplacer',
      optionKey: calfRearingTypes[1]?.key,
      info: 'l Milchaustauschertränke je Tier und Tag',
      dataSource: 'Tränkekurve',
      warningMax: 15,
      unit: 'l',
      errorMax: 20
    },
    {
      label: t('Calf conc. feed'),
      key: 'calfConcentratedFeed',
      dataSource: 'Rationsberechnung/Lieferzettel',
      unit: 'kg/Tag',
      warningMax: 10,
      errorMax: 99
    }
  ]

  const [tableColumnHeaders, setTableColumnHeaders] = useState(defaltTableColumHeaders)

  useEffect(() => {
    const filteredTableColumnHeaders = getTableColumnHeaders()
    setTableColumnHeaders(filteredTableColumnHeaders)
  }, [watchCalfRearing])

  function getTableColumnHeaders() {
    switch(watchCalfRearing) {
      case VOL_KEY:
        return defaltTableColumHeaders.filter(column => column?.optionKey !== MAT_KEY)
      case MAT_KEY:
        return defaltTableColumHeaders.filter(column => column?.optionKey !== VOL_KEY)
      case VOL_MAT_KEY:
        return defaltTableColumHeaders
      default:
        return defaltTableColumHeaders
    }
  }

  function renderHeaders() {
    return tableColumnHeaders.map((header) => {
      return <Table.HeaderCell
        textAlign='center'
        key={header.key}
      >
        <div style={{ display: 'flex', gap: 5 }}>
          <div>{header.label}</div>
          {header?.info && <InfoPopup
            content={header.info}
            position={'top center'}
            styleIcon={{ fontSize: '1.2em', color: 'white' }}
          />}
          {header.dataSource && <LinkPopup
            content={header.dataSource}
            styleIcon={{ color: 'white' }}
          />}
        </div>
      </Table.HeaderCell>
    })
  }

  const renderTableBody = useCallback(() => {
    const slicedArray = cloneDeep(fields).slice(0, parseInt(watchWeaningWeeks))
  
    return slicedArray?.map((calfRearing, indexRow) => {
      return (
        <Table.Row key={`calf-rearing-${indexRow}`}>
          {tableColumnHeaders.map((header) => {
            if (header.key === 'feedingSection') {
              return (
                <Table.Cell key={header.label + indexRow} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {`${indexRow + 1}. Woche`}
                  <ErrorIcon
                    control={control}
                    name={`weaningWeeksValues[${indexRow}].${header.key}`}
                  />
                </Table.Cell>
              )
            }

            return (
              <Table.Cell key={header.label + header.key + indexRow} textAlign='center'>
                <FormFields.Input
                  control={control}
                  typeInput='controlled-inline-error'
                  disabled={isLoadingPredefinedData}
                  decimalScale={2}
                  suffix={header.unit}
                  allowDecimals
                  rules={{
                    required: true,
                    validate: (value) => {
                      const transformedValue = parseFloat(value.toString().replace(',', '.'))
                      const isMillColumn = header.key === 'wholeMilk' || header.key === 'milkReplacer'
                      if(transformedValue < 0 || transformedValue > header.errorMax) {
                        return isMillColumn
                          ? 'Die Tränkemenge darf maximal 20 l pro Tag betragen.'
                          : false
                      }
                      return true
                    }
                  }}
                  warningMax={{
                    value: header?.warningMax,
                    message: 'Die Tränkemenge liegt normalerweise unter 15 l pro Tag.'
                  }}
                  noErrorMessage
                  withoutAsterisk
                  name={`weaningWeeksValues[${indexRow}].${header.key}`}
                  value={calfRearing[header.key]}
                  placeholder={`in ${header.unit}`}
                />
              </Table.Cell>
            )
          })}
        </Table.Row>
      )
    })
  }, [watchWeaningWeeks, fields, tableColumnHeaders, isLoadingPredefinedData])

  function renderCalfRearingWeeks() {
    return <Table
      celled
      compact
      striped
      style={{ marginTop: 0, minWidth: 800 }}
    >
      <Table.Header>
        <Table.Row>
          {renderHeaders()}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table>
  }

  function renderCalfType() {
    return <Grid style={{ marginBottom: '0.5em' }}>
    </Grid>
  }

  return <div style={{ display: 'flex', flexDirection: 'column' }}>
    {renderCalfType()}
    {watchWeaningWeeks && watchCalfRearing && renderCalfRearingWeeks()}
  </div>
}
export default TableRearing